import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { getPaymentCollectionsAdvancedApiBaseUrl } from '../../../core/reconciliation-api-url';

/** Una corrida de integración (Edge): programada o recarga manual pedida desde la web. */
export interface IntegrationRun {
  id: string;
  connector: string;
  jobName: string;
  sourceCode?: string | null;
  trigger: 'Scheduled' | 'Manual' | string;
  status: 'Requested' | 'Running' | 'Succeeded' | 'Failed' | string;
  requestedAt: string;
  requestedBy?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  recordsExtracted: number;
  recordsLoaded: number;
  recordsFailed: number;
  errorMessage?: string | null;
  processedThrough?: string | null;
  replace?: boolean;
}

/** Estado de un job según su última corrida. */
export interface IntegrationJobHealth {
  connector: string;
  jobName: string;
  sourceCode?: string | null;
  health: 'Ok' | 'Failed' | 'Running' | 'Pending' | string;
  lastRun: IntegrationRun;
  lastSuccessAt?: string | null;
  failuresLast24h: number;
  hasPendingReload: boolean;
}

export interface RequestIntegrationReload {
  connector: string;
  jobName: string;
  dateFrom: string;
  dateTo: string;
  /** Borra lo ya cargado en cada semana del rango antes de cargar lo extraído. */
  replace?: boolean;
}

/** Contrato de la llave documental de una fuente. */
export interface DocumentKeyContract {
  sourceCode: string;
  mode: 'AsSent' | 'PrefixNumber' | string;
  prefixMap: Record<string, string>;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Revisión de las llaves guardadas de un periodo. */
export interface DocumentKeyCheck {
  sourceCode: string;
  period: string;
  documents: number;
  keysOutsideContract: number;
  withoutNumber: number;
  repeatedDocuments: number;
  examples: string[];
}

/** Rango máximo de una recarga manual (igual que el backend). */
export const MAX_RELOAD_DAYS = 93;

@Injectable({ providedIn: 'root' })
export class IntegrationRunsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = getPaymentCollectionsAdvancedApiBaseUrl();

  health(): Observable<IntegrationJobHealth[]> {
    return this.http.get<IntegrationJobHealth[]>(`${this.apiBase}/api/integration-runs/health`);
  }

  history(jobName?: string, take = 20): Observable<IntegrationRun[]> {
    const params: Record<string, string | number> = { take };
    if (jobName) params['jobName'] = jobName;
    return this.http.get<IntegrationRun[]>(`${this.apiBase}/api/integration-runs`, { params });
  }

  requestReload(body: RequestIntegrationReload): Observable<IntegrationRun> {
    return this.http.post<IntegrationRun>(`${this.apiBase}/api/integration-runs/reloads`, body);
  }

  keyContract(sourceCode: string): Observable<DocumentKeyContract> {
    return this.http.get<DocumentKeyContract>(`${this.apiBase}/api/source-documents/key-contract/${encodeURIComponent(sourceCode)}`);
  }

  saveKeyContract(sourceCode: string, body: { mode: string; prefixMap: Record<string, string> }): Observable<DocumentKeyContract> {
    return this.http.put<DocumentKeyContract>(`${this.apiBase}/api/source-documents/key-contract/${encodeURIComponent(sourceCode)}`, body);
  }

  checkKeys(sourceCode: string, period: string): Observable<DocumentKeyCheck> {
    return this.http.get<DocumentKeyCheck>(`${this.apiBase}/api/source-documents/key-contract/${encodeURIComponent(sourceCode)}/check`, { params: { period } });
  }
}
